import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton } from '@material-ui/core';
import {
  HeadsetMic,
  Close as CloseIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
} from '@material-ui/icons';
import * as lib from '~/dependencies';
import { useControlSessions } from '~/hooks';
import * as actions from '~/store/actions';
import * as actionTypes from '~/store/actions/actionTypes';
import DeviceManager from '../DeviceManager';

import { Dialog, DialogContent, DialogTitle, Button } from './styles';

export default function HeadsetManager() {
  // HOOKS
  const controlSessions = useControlSessions();
  const dispatch = useDispatch();
  const { t } = lib.useTranslation();

  const currentCalls = useSelector(state => state.callCurrent?.data);
  const isConference = currentCalls?.length
    ? !!currentCalls.filter(c => c.status === actionTypes.CALL_CONFERENCE)
        ?.length
    : false;

  const isMuted = currentCalls?.length
    ? !!currentCalls.filter(c => c.mute === true)?.length
    : false;

  // The dialog is opened from outside as well as from the button below - the microphone warning
  // toast links straight into it - and a toast is rendered outside this tree by react-toastify,
  // so the open state has to live in redux rather than in a local `useState`.
  const openDialog = useSelector(state => state.system.voiceSettingsOpen);

  // This component only exists while a call does (`ActiveCall` is gated on the call list), so a
  // dialog left open when the last call ends would re-open itself on the next one.
  useEffect(
    () => () => {
      dispatch(actions.setVoiceSettingsOpen(false));
    },
    [dispatch]
  );

  function handleOpenHeadsetManager() {
    dispatch(actions.setVoiceSettingsOpen(!openDialog));
  }

  function handleConferenceMicState(nextMicState) {
    if (currentCalls?.length) {
      currentCalls.forEach(c => {
        if (c.id) {
          controlSessions(c.id, nextMicState);
        }
      });
    }
  }

  return (
    <>
      <div className="buttons-container">
        {isConference && (
          <>
            {isMuted ? (
              <Button
                aria-controls="fade-menu"
                aria-haspopup="true"
                disableRipple
                onClick={() => handleConferenceMicState('unmute')}>
                <MicOffIcon
                  titleAccess={t('components.activeCalls.btn.unmute')}
                />
              </Button>
            ) : (
              <Button
                aria-controls="fade-menu"
                aria-haspopup="true"
                disableRipple
                onClick={() => handleConferenceMicState('mute')}>
                <MicIcon titleAccess={t('components.activeCalls.btn.mute')} />
              </Button>
            )}
          </>
        )}

        <Button
          data-testid="headset-manager-button"
          aria-controls="fade-menu"
          aria-haspopup="true"
          onClick={handleOpenHeadsetManager}
          disableRipple>
          <HeadsetMic
            titleAccess={t('components.headsetManager.titleAccess')}
          />
        </Button>
      </div>
      <Dialog
        open={!!openDialog}
        onClose={() => dispatch(actions.setVoiceSettingsOpen(false))}
        aria-labelledby="customized-dialog-title">
        <DialogTitle id="customized-dialog-title" disableTypography>
          <span>{t('components.headsetManager.title')}</span>
          <IconButton
            aria-label="close"
            style={{ padding: 3 }}
            onClick={handleOpenHeadsetManager}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DeviceManager disableRingDevice disableAudioTest showSpeakerVolume />
        </DialogContent>
      </Dialog>
    </>
  );
}
